
import { Box, Button, Stack, Tab, Tabs, ToggleButton, ToggleButtonGroup, Typography, useMediaQuery } from '@mui/material'
import { a11yProps } from '../../../../shared/utils/misc-shared-utils'
import { Add, DownloadOutlined, GridView, MenuOutlined } from '@mui/icons-material'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { ReportPeriodContext } from '../../../../context/report-periods/report-periods'
import { useNavigate, useLocation } from 'react-router-dom'
import gaiaTheme from '../../../../root-components/theme'
import { CustomTabPanel } from '../../../presentation-components/display-elements-components/custom-tab-panel';
import { EmissionsAssumptionsTable } from '../../../presentation-components/emissons-components/overview/emissions-assumptions-table'
import { PageOuterContainer } from '../../../../root-components/layouts/page-outer-container'
import { PageTitle } from '../../../presentation-components/display-elements-components/page-title'
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications'
import FileDownloadService from '../../../../shared/services/file-download/file-download.service'
import { DownloadStatusContext } from '../../../../context/download-status/download-status'
import { Emissions } from '../../../presentation-components/emissons-components/overview/emissions'
import { UserDetailsContext } from '../../../../context/user-details/user-details'
import { userHasRequiredRole } from '../../../../shared/utils/role-utils'
import { AddEmissionGroupPickerDialog } from '../../../dialogs/emissions/add-emission-group-picker-dialog'
import { IGroup } from '../../../../shared/services/group/group.model'
import GroupService from '../../../../shared/services/group/group.service'
import { useHasOpenReportPeriod } from '../../../../hooks/use-has-open-report-period'
import { NotesPanel } from '../../../presentation-components/notes-components/notes-panel'
import { getSelectableGroups, groupOptionLabel } from '../../../../shared/utils/group-options'

export const EmissionsOverviewTablePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  //Context
  const reportPeriodContext = useContext(ReportPeriodContext);
  const userNotificationsContext = useContext(UserNotificationsContext)
  const downloadStatusContext = useContext(DownloadStatusContext)
  const userDetailsContext = useContext(UserDetailsContext)
  // Full-org exports are admin/analyst only and refused for scoped users on the BE; don't show
  // buttons that would only 401/403 for a data owner.
  const canDownload = userHasRequiredRole(userDetailsContext.userDetails, ['admin', 'analyst'])
  // Period notes are written by admins and analysts; a data owner can read them.
  const canWriteNotes = userHasRequiredRole(userDetailsContext.userDetails, ['admin', 'analyst'])
  // The assumptions register is admin and analyst only (the /assumption API refuses anyone else),
  // so the tab is not shown to a data owner. Notes moves up a place when it is hidden.
  const showAssumptionsTab = userHasRequiredRole(userDetailsContext.userDetails, ['admin', 'analyst'])
  const assumptionsTabIndex = showAssumptionsTab ? 1 : -1
  const notesTabIndex = showAssumptionsTab ? 2 : 1
  //Services
  const fileDownloadService = new FileDownloadService()
  const groupService = GroupService.getInstance()
  //State
  const [page, setPage] = React.useState<string>('/emissions/overview/table');
  const [tabValue, setTabValue] = useState<number>(0)
  // The quick-add picker needs the org's groups, the same set the group view lists.
  const [groups, setGroups] = useState<IGroup[]>([])
  // The note box's optional Group list: the same groups the Add Assumption dialog offers.
  const noteGroupOptions = useMemo(
    () => getSelectableGroups(groups).map(group => ({ id: group.id, label: groupOptionLabel(group) })),
    [groups],
  )
  const [addEmissionDialogOpen, setAddEmissionDialogOpen] = useState(false)
  //Layout
  const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('md'));
  //Hooks
  const hasOpenReport = useHasOpenReportPeriod([
    reportPeriodContext.allReportPeriods,
    reportPeriodContext.lastSelectedReportId,
  ]);

  useEffect(() => {
    if (!reportPeriodContext.lastSelectedReportId) return
    groupService.getGroupsAndActionInfoForReportPeriod(reportPeriodContext.lastSelectedReportId)
      .then(response => setGroups(response))
      .catch(err => console.log(err))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportPeriodContext.lastSelectedReportId])


  const pageHandler = (
    event: React.MouseEvent<HTMLElement>,
    page: string | null,
  ) => {
    if (page) {
      setPage(page);
      navigate(page)
    }
  };

  const changeTabHandler = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // The server explains why a download was refused: an account limited to specific locations or
  // subsidiaries cannot generate an organisation-wide report, and the message says who to ask.
  // Replacing that with "there was an error" sent people to support for something they could have
  // resolved themselves, so the server's own words come through whenever it sends any.
  const startDownload = (type: string) => {
    if (!reportPeriodContext.lastSelectedReportId) {
      return
    }
    fileDownloadService.createFileDownloadEntry(type, reportPeriodContext.lastSelectedReportId, null)
      .then(response => {
        downloadStatusContext.startDownloadTracking(response.id)
      })
      .catch(err => {
        console.log(err)
        const message = err?.response?.data?.message || 'There was an error, please try again'
        userNotificationsContext.openSnackbar({ open: true, message, backgroundColor: 'text.primary' });
      })
  }

  const downloadCsvHandler = (e: React.MouseEvent<HTMLElement>) => startDownload('emissionsCsv')

  const downloadAuditPackHandler = (e: React.MouseEvent<HTMLElement>) => startDownload('auditPackXlsx')


  return (
    <PageOuterContainer>
      <Stack
        direction={isMobileMode ? 'column' : 'row'}
        justifyContent='space-between'
        alignItems={isMobileMode ? 'stretch' : 'center'}
        spacing={isMobileMode ? 2 : 0}
        sx={{ mb: 1, width: '100%' }}
      >
        <PageTitle title={'Emissions'} />
        <Stack
          direction={isMobileMode ? 'column' : 'row'}
          alignItems={isMobileMode ? 'stretch' : 'center'}
          spacing={isMobileMode ? 1.5 : 0}
          sx={{ width: isMobileMode ? '100%' : 'auto', flexShrink: 0 }}
        >
          {hasOpenReport &&
            /* Never disabled on load: groups arrive a moment after paint, and flipping a
               contained button from grey to green reads as a glitch. The dialog says it is
               still loading in the rare window where they have not arrived. */
            <Button
              variant='contained'
              onClick={() => setAddEmissionDialogOpen(true)}
              fullWidth={isMobileMode}
              sx={{
                mr: isMobileMode ? 0 : 1,
                minHeight: isMobileMode ? 44 : undefined,
                py: isMobileMode ? 1.125 : undefined,
              }}
            >
              <Add />
              <Typography sx={{ display: 'inline' }}>Add Emission</Typography>
            </Button>}
          {canDownload && <>
          <Button
            onClick={downloadCsvHandler}
            variant='outlined'
            startIcon={<DownloadOutlined />}
            fullWidth={isMobileMode}
            sx={{
              mr: isMobileMode ? 0 : 1,
              minHeight: isMobileMode ? 44 : undefined,
              py: isMobileMode ? 1.125 : undefined,
            }}
          >
            <Typography sx={{ display: 'inline' }}>Download CSV</Typography>
          </Button>
          <Button
            onClick={downloadAuditPackHandler}
            variant='outlined'
            startIcon={<DownloadOutlined />}
            fullWidth={isMobileMode}
            sx={{
              mr: isMobileMode ? 0 : 1,
              minHeight: isMobileMode ? 44 : undefined,
              py: isMobileMode ? 1.125 : undefined,
            }}
          >
            <Typography sx={{ display: 'inline' }}>Audit Pack</Typography>
          </Button>
          </>}
          <ToggleButtonGroup
            value={page}
            exclusive
            onChange={pageHandler}
            orientation='horizontal'
            sx={{
              display: 'flex',
              width: isMobileMode ? '100%' : 'auto',
              alignSelf: isMobileMode ? 'stretch' : 'flex-end',
              '& .MuiToggleButton-root': isMobileMode
                ? { flex: 1, py: 1.125, minHeight: 44 }
                : {},
            }}
          >
            <ToggleButton
              value="/emissions/overview"
              aria-label="Group view"
              sx={{ width: isMobileMode ? undefined : '50px', pt: 0.5, pb: 0.5, textTransform: 'capitalize' }}
            >
              <GridView />
            </ToggleButton>
            <ToggleButton
              value="/emissions/overview/table"
              aria-label="Table view"
              sx={{ width: isMobileMode ? undefined : '50px', pt: 0.5, pb: 0.5, textTransform: 'none' }}
            >
              <MenuOutlined />
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Stack>
      {addEmissionDialogOpen &&
        <AddEmissionGroupPickerDialog
          open={addEmissionDialogOpen}
          closeHandler={() => setAddEmissionDialogOpen(false)}
          groups={groups}
        />}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Stack direction={'row'} justifyContent={'space-between'}>
          <Tabs value={tabValue} onChange={changeTabHandler} aria-label="tabs">
            <Tab label="Emissions" {...a11yProps(0)} />
            {showAssumptionsTab && <Tab label="Assumptions" {...a11yProps(assumptionsTabIndex)} />}
            <Tab label="Notes" {...a11yProps(notesTabIndex)} />
          </Tabs>
        </Stack>
      </Box>
      <CustomTabPanel value={tabValue} index={0}>
        <Emissions includeDelete={true} includeEdit={true} queryString={location.search ? location.search.replace(/^\?/, '') : undefined} />
      </CustomTabPanel>
      {showAssumptionsTab &&
        <CustomTabPanel value={tabValue} index={assumptionsTabIndex}>
          <EmissionsAssumptionsTable />
        </CustomTabPanel>}
      <CustomTabPanel value={tabValue} index={notesTabIndex}>
        {reportPeriodContext.lastSelectedReportId && (
          <NotesPanel
            relatedModelName='reportPeriod'
            relatedObjectId={reportPeriodContext.lastSelectedReportId}
            reportPeriodId={reportPeriodContext.lastSelectedReportId}
            canWrite={canWriteNotes}
            groupOptions={noteGroupOptions}
            onAddAssumption={showAssumptionsTab ? () => setTabValue(assumptionsTabIndex) : undefined}
          />
        )}
      </CustomTabPanel>
    </PageOuterContainer>
  )
}